/**
 * 檔案用途區塊
 * @module AppKitProvider
 * @purpose 統一封裝 Reown AppKit、Wagmi 與 React Query 的初始化流程，並依 tokens 動態組合可用網路與 adapter，提供錢包登入所需的 Provider 邊界。
 * @external https://innotech.atlassian.net/browse/IN-111885
 * @external https://innotech.atlassian.net/browse/IN-111892
 * @external https://innotech.atlassian.net/browse/IN-120084
 * @external https://innotech.atlassian.net/browse/IN-120073
 * @external https://innotech.atlassian.net/browse/IN-121918
 */
import React, { useMemo } from 'react';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createAppKit } from '@reown/appkit/react';
import { AppKitNetwork, mainnet } from '@reown/appkit/networks';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import {
  getNetworksFromTokens,
  getAdapterTypesFromTokens,
} from '@modules/walletconnect/config/token-network-adapter-mapping';
import type { AdapterType } from '@modules/walletconnect/config/token-network-adapter-mapping';
import { createAdapters, isEVMNetwork } from './adapter-factory';

/**
 * 宣告內容用途說明與單號關聯
 * @description 建立全域共用的 React Query Client，避免 Provider 重複初始化造成快取分裂。
 * @purpose 維持 App 內查詢狀態一致性。
 * @external https://innotech.atlassian.net/browse/IN-111885
 */
const queryClient = new QueryClient();

/**
 * 宣告內容用途說明與單號關聯
 * @description 定義 AppKit 對外顯示的應用 metadata，供錢包端與 Reown 後台識別。
 * @purpose 提供連線流程中的應用識別資訊。
 * @external https://innotech.atlassian.net/browse/IN-111885
 */
const metadata = {
  name: 'ThirdParty SSO',
  description: 'WalletConnect login for Fluid',
  url: window.location.origin,
  icons: [],
};

/**
 * 宣告內容用途說明與單號關聯
 * @description 設定未傳入 tokens 或無對應網路時的預設鏈清單。
 * @purpose 確保 AppKit 初始化時至少具備可用 EVM 網路。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
const defaultNetworks = [mainnet] as [AppKitNetwork, ...AppKitNetwork[]];

/**
 * 宣告內容用途說明與單號關聯
 * @description 保存已建立的 WagmiAdapter 實例，供後續 Provider 直接重用。
 * @purpose 避免重複產生 adapter 導致設定不一致。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
let globalWagmiAdapter: WagmiAdapter | null = null;

/**
 * 宣告內容用途說明與單號關聯
 * @description 記錄 createAppKit 是否已執行過初始化。
 * @purpose 滿足 AppKit 單次初始化限制。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
let appKitInitialized = false;

/**
 * 宣告內容用途說明與單號關聯
 * @description 在 createAppKit 之前清除 WalletConnect、AppKit 與 Wagmi 的持久化 session/連線資料，避免舊連線在 SDK 啟動時被還原。
 * @purpose 降低 session 還原時序競爭造成的連線異常與誤斷線風險。
 * @external https://innotech.atlassian.net/browse/IN-120084
 */
function clearPersistedWalletConnectSessions(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('wc@2') || key.startsWith('@appkit') || key === 'wagmi' || key.startsWith('wagmi.'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依據 tokens 推導 networks 與 adapter 組合，完成 AppKit 與 WagmiAdapter 的單次初始化並回傳主 adapter。
 * @purpose 建立可被 WagmiProvider 使用的錢包連線核心設定。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
function initializeAppKit(tokens: string[], clientId: string): WagmiAdapter {
  // 如果已經初始化過，不重複初始化
  if (appKitInitialized && globalWagmiAdapter) {
    return globalWagmiAdapter;
  }

  clearPersistedWalletConnectSessions();

  // 根據 tokens 取得對應的 networks
  let networks: AppKitNetwork[];
  if (tokens.length === 0) {
    // 如果沒有提供 tokens，使用預設網路
    networks = defaultNetworks;
  } else {
    networks = getNetworksFromTokens(tokens);
    if (networks.length === 0) {
      networks = defaultNetworks;
    }
  }

  // 確保至少有一個 network
  const finalNetworks = networks.length > 0 ? (networks as [AppKitNetwork, ...AppKitNetwork[]]) : defaultNetworks;

  // 根據 tokens 取得需要的 adapter 類型
  const adapterTypes: AdapterType[] = tokens.length > 0 ? getAdapterTypesFromTokens(tokens) : ['wagmi'];

  // 根據 adapterType 分組 networks
  const networksByAdapterType = new Map<AdapterType, AppKitNetwork[]>();
  finalNetworks.forEach((network) => {
    if (isEVMNetwork(network)) {
      const existing = networksByAdapterType.get('wagmi') || [];
      networksByAdapterType.set('wagmi', [...existing, network]);
    }
    // TODO: 未來支援 Solana 和 TON 時，需要加入對應的判斷
  });

  // 確保如果 adapterTypes 包含 'wagmi'，但 networksByAdapterType 中沒有 'wagmi' 的 networks，
  // 則使用預設網路（避免 WagmiAdapter 初始化失敗）
  if (
    adapterTypes.includes('wagmi') &&
    (!networksByAdapterType.has('wagmi') || networksByAdapterType.get('wagmi')?.length === 0)
  ) {
    networksByAdapterType.set('wagmi', defaultNetworks);
  }

  // 建立 adapters
  const adapters = createAdapters(adapterTypes, networksByAdapterType, clientId);

  // 確保至少有一個 adapter（通常是 WagmiAdapter）
  if (adapters.length === 0) {
    const defaultWagmiAdapter = new WagmiAdapter({
      networks: defaultNetworks,
      projectId: clientId,
      ssr: false,
    });
    adapters.push(defaultWagmiAdapter);
    globalWagmiAdapter = defaultWagmiAdapter;
  } else {
    // 找到第一個 WagmiAdapter 作為主要的 adapter（用於 WagmiProvider）
    const wagmiAdapter = adapters.find((adapter) => adapter instanceof WagmiAdapter) as WagmiAdapter | undefined;
    if (wagmiAdapter) {
      globalWagmiAdapter = wagmiAdapter;
    } else {
      // 如果沒有 WagmiAdapter，建立一個預設的（用於 Provider）
      const defaultWagmiAdapter = new WagmiAdapter({
        networks: defaultNetworks,
        projectId: clientId,
        ssr: false,
      });
      adapters.push(defaultWagmiAdapter);
      globalWagmiAdapter = defaultWagmiAdapter;
    }
  }

  // 透過 createAppKit 建立 AppKit modal
  // 注意：createAppKit 只能呼叫一次，後續呼叫會被忽略
  createAppKit({
    adapters,
    networks: finalNetworks,
    projectId: clientId,
    metadata,
    enableMobileFullScreen: true,
    // 隱藏 Coinbase wallet（IN-120073）：使用官方 excludeWalletIds，避免登入/註冊報錯 10208
    excludeWalletIds: [
      'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa', // Base (new coinbase wallet)
      'd0ca99ff52b99abc48743dad0f7fc891e041be73574f7fac4afe5d4bb83845c8', // Coinbase Wallet
    ],
    features: {
      analytics: true,
      email: false,
      socials: false,
    },
  });

  appKitInitialized = true;

  return globalWagmiAdapter!;
}

interface AppKitProviderProps {
  tokens: string[];
  clientId: string;
  children: React.ReactNode;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 建立 React Provider 組合，於初次渲染時計算並注入 AppKit/Wagmi 設定。
 * @purpose 讓子元件可共享錢包連線能力與查詢快取上下文。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
const AppKitProvider: React.FC<AppKitProviderProps> = ({ children, tokens, clientId }) => {
  // 初始化 AppKit（只會初始化一次）
  const adapter = useMemo(() => {
    return initializeAppKit(tokens, clientId);
  }, [tokens, clientId]);

  return (
    <WagmiProvider config={adapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </WagmiProvider>
  );
};

export default AppKitProvider;

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:20:37.468Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note 已完成註解重構：新增頂部「檔案用途區塊」、為主要宣告補上「宣告內容用途說明與單號關聯」JSDoc（含對應 Jira URL）、移除介面屬性冗長註解以避免非必要模板描述，並於檔尾新增「llm 分析紀錄區」。程式執行邏輯未變更。
 */
